// =============================================================================
// features/sales/agenda/buckets.ts — Regras de recorte temporal da agenda
// (doc 26 §6.1: "o que venceu, o que é agora, o que é hoje e o que vem").
//
// O contrato de API (F33-S02) só oferece filtro por intervalo
// (scheduledFrom/scheduledTo) + status — não existe um endpoint de
// "buckets". O doc 26 define as 4 abas e os 4 indicadores por NOME, mas não
// fixa a janela exata de "agora" nem se os números da faixa de indicadores
// se sobrepõem às abas. Decisão de produto registrada aqui (e no PR, por
// instrução do padrão de UI do time):
//
//   - Vencidos:  status=scheduled E scheduledAt < agora.
//   - Agora:     status=scheduled E scheduledAt <= agora + AGORA_WINDOW_MIN.
//                Superset deliberado de "vencidos" — um retorno atrasado
//                continua exigindo ação AGORA (doc 26 §10: "Retorno vencido:
//                agrupar para evitar ruído e elevar visualmente na Agenda").
//                Só precisa do limite superior (scheduledTo): a API já
//                inclui tudo no passado por não ter scheduledFrom.
//   - Hoje:      status=scheduled E scheduledAt dentro do dia civil de hoje
//                (fuso da organização) — janela fixa, pode se sobrepor a
//                "Agora" (é uma foto do dia inteiro, não uma partição).
//   - Próximos:  status=scheduled E scheduledAt após o fim do dia de hoje.
//                O indicador da faixa usa um teto de 7 dias corridos (nome
//                do indicador, doc 26 §6.1); a ABA "Próximos" não aplica
//                esse teto — mostra todo o futuro, paginado, para não
//                esconder compromissos legítimos além da semana.
//   - Concluídos: status IN (completed, cancelled), mais recentes primeiro.
//
// Todas as funções são puras (recebem `now` explícito) — testáveis sem
// mockar relógio global. Ver __tests__/buckets.test.ts.
// =============================================================================

import type { AppointmentStatus } from '@elemento/shared-schemas';

import type { AppointmentsFilters } from '../../../hooks/sales/useAppointments';

import { addDays, addMinutes, endOfDayOrgTz, startOfDayOrgTz } from './timezone';

/** Janela de "imediatismo" da aba/indicador Agora — decisão de produto, não do doc 26. */
export const AGORA_WINDOW_MINUTES = 60;

/** Teto do indicador "próximos 7 dias" (só o número da faixa — a aba Próximos não usa este teto). */
export const PROXIMOS_INDICATOR_DAYS = 7;

export type AgendaTab = 'agora' | 'hoje' | 'proximos' | 'concluidos';

export const AGENDA_TABS: { value: AgendaTab; label: string }[] = [
  { value: 'agora', label: 'Agora' },
  { value: 'hoje', label: 'Hoje' },
  { value: 'proximos', label: 'Próximos' },
  { value: 'concluidos', label: 'Concluídos' },
];

type TimeWindowFilters = Pick<
  AppointmentsFilters,
  'status' | 'scheduledFrom' | 'scheduledTo' | 'sortBy' | 'sortOrder'
>;

const SCHEDULED: AppointmentStatus[] = ['scheduled'];
const RESOLVED: AppointmentStatus[] = ['completed', 'cancelled'];

/** Filtro de janela de tempo para o indicador "Vencidos" (status=scheduled, scheduledAt < now). */
export function vencidosWindow(now: Date): TimeWindowFilters {
  return {
    status: SCHEDULED,
    scheduledTo: now.toISOString(),
    sortBy: 'scheduledAt',
    sortOrder: 'asc',
  };
}

/** Filtro de janela de tempo para a aba/indicador "Agora" (vencidos ∪ próximos AGORA_WINDOW_MINUTES min). */
export function agoraWindow(now: Date): TimeWindowFilters {
  return {
    status: SCHEDULED,
    scheduledTo: addMinutes(now, AGORA_WINDOW_MINUTES).toISOString(),
    sortBy: 'scheduledAt',
    sortOrder: 'asc',
  };
}

/** Filtro de janela de tempo para a aba/indicador "Hoje" (dia civil completo, fuso da organização). */
export function hojeWindow(now: Date): TimeWindowFilters {
  return {
    status: SCHEDULED,
    scheduledFrom: startOfDayOrgTz(now).toISOString(),
    scheduledTo: endOfDayOrgTz(now).toISOString(),
    sortBy: 'scheduledAt',
    sortOrder: 'asc',
  };
}

/** Filtro de janela de tempo para o INDICADOR "próximos 7 dias" (teto — só o número da faixa). */
export function proximosIndicatorWindow(now: Date): TimeWindowFilters {
  const startOfTomorrow = new Date(endOfDayOrgTz(now).getTime() + 1);
  return {
    status: SCHEDULED,
    scheduledFrom: startOfTomorrow.toISOString(),
    scheduledTo: addDays(startOfTomorrow, PROXIMOS_INDICATOR_DAYS).toISOString(),
    sortBy: 'scheduledAt',
    sortOrder: 'asc',
  };
}

/** Filtro de janela de tempo para a ABA "Próximos" — todo o futuro após hoje, sem teto (paginado). */
export function proximosTabWindow(now: Date): TimeWindowFilters {
  const startOfTomorrow = new Date(endOfDayOrgTz(now).getTime() + 1);
  return {
    status: SCHEDULED,
    scheduledFrom: startOfTomorrow.toISOString(),
    sortBy: 'scheduledAt',
    sortOrder: 'asc',
  };
}

/** Filtro para a aba "Concluídos" — concluído ou cancelado, mais recente primeiro. */
export function concluidosWindow(): TimeWindowFilters {
  return {
    status: RESOLVED,
    sortBy: 'scheduledAt',
    sortOrder: 'desc',
  };
}

/** Resolve o filtro de janela de tempo da aba ativa. */
export function windowForTab(tab: AgendaTab, now: Date): TimeWindowFilters {
  switch (tab) {
    case 'agora':
      return agoraWindow(now);
    case 'hoje':
      return hojeWindow(now);
    case 'proximos':
      return proximosTabWindow(now);
    case 'concluidos':
      return concluidosWindow();
  }
}
